/*
Copyright (C) 2024 Laboratorio Visao Robotica e Imagem

Departamento de Informatica - Universidade Federal do Parana - VRI/UFPR

This file is part of CienciaNaEscola. CienciaNaEscola is free software: you can redistribute it and/or modify it under the terms of the GNU
General Public License as published by the Free Software Foundation, either version 3 of the License, or (at your option) any later version.
CienciaNaEscola is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU General Public License for more details. You should have received a copy
of the GNU General Public License along with CienciaNaEscola.  If not, see <https://www.gnu.org/licenses/>
*/

import { useState, useEffect, useContext, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import SplashPage from './SplashPage';
import NavBar from '../components/Navbar';
import { AuthContext } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import { Chart } from 'react-google-charts';
import Gallery from '../components/Gallery';
import GalleryModal from '../components/GalleryModal';
import ErrorPage from './ErrorPage';
import TableInput from '../components/inputs/answers/TableInput';
import TextButton from '../components/TextButton';
import { AlertContext } from '../contexts/AlertContext';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import RoundedButton from '../components/RoundedButton';
import MaterialSymbol from '../components/MaterialSymbol';

const styles = `
    .bg-yellow-orange {
        background-color: #FECF86;
    }

    .color-yellow-orange {
        color: #FECF86;
    }

    .bg-light-gray {
        background-color: #D9D9D9;
    }

    .bg-coral-red {
        background-color: #F59489;
    }

    .color-gray {
        color: #787878 !important;
    }

    .font-century-gothic {
        font-family: 'Century Gothic', sans-serif;
    }

    .color-dark-gray {
        color: #535353 !important;
    }

    .font-barlow {
        font-family: 'Barlow', sans-serif;
    }

    @media (min-width: 992px) {
      .position-lg-sticky {
        position: sticky !important;
        top: 0;
      }
    }
`;

/**
 * Página que exibe as respostas de uma aplicação específica.
 * @param {Object} props - Propriedades do componente.
 */
function AnswerPage(props) {
    const { applicationId } = useParams();
    const [error, setError] = useState(undefined);
    const [answer, setAnswer] = useState();
    const [selectedAnswer, setSelectedAnswer] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState(undefined);
    const [filterStatus, setFilterStatus] = useState('ALL');
    const { user } = useContext(AuthContext);
    const { showAlert } = useContext(AlertContext);
    const modalRef = useRef(null);
    const galleryModalRef = useRef(null);
    const mapRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');

    /**
     * Define a visualização da resposta e a questão selecionadas.
     * @param {Object} person - Resposta do usuário.
     * @param {Object} question - Pergunta associada à resposta.
     */
    const setVisualization = (person, question) => {
        setSelectedAnswer(person);
        setSelectedItem(question);
    };

    useEffect(() => {
        if (isLoading && user.status !== 'loading') {
            axios
                .get(`${process.env.REACT_APP_API_URL}api/application/getApplicationWithAnswers/${applicationId}`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                })
                .then((response) => {
                    setAnswer(response.data.data);
                    setIsLoading(false);
                })
                .catch((error) =>
                    setError({ text: 'Erro ao obter respostas da aplicação', description: error.response?.data.message || '' })
                );
        }
    }, [applicationId, isLoading, user.status, user.token]);

    if (error) return <ErrorPage text={error.text} description={error.description} />;

    if (isLoading) return <SplashPage text="Carregando respostas da aplicação..." />;

    /**
     * Função de exportar csv.
     * @param {React.RefObject} modalRef - Referência ao modal para exibição de alertas.
     */
    const createFile = (modalRef) => {
        let obj, csv;

        obj = answer;

        if (Object.keys(obj.answers).length > 0) {
            csv = jsonToCsv(obj);

            var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            if (navigator.msSaveBlob) {
                navigator.msSaveBlob(blob, 'data.csv');
            } else {
                var link = document.createElement('a');
                if (link.download !== undefined) {
                    var url = URL.createObjectURL(blob);
                    link.setAttribute('href', url);
                    link.setAttribute('download', 'data.csv');
                    link.style.visibility = 'hidden';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            }
        } else showAlert({ headerText: 'O seguinte protocolo não possui respostas: ' + obj.protocol.title });
    };

    /**
     * Converte um objeto JSON contendo respostas em uma string CSV formatada.
     * @param {Object} ans Objeto contendo as respostas do usuário.
     */
    const jsonToCsv = (ans) => {
        const statusMapping = {
            REJECTED: 'reprovada',
            APPROVED: 'aprovada',
            PENDING: 'pendente',
        };

        let header = ['username', 'approvalStatus'],
            answers = [];
        const ansIds = Object.keys(ans.answers);
        const protocol = ans.protocol;

        let totalItems = 0;
        let countTables = 0;

        /** Armazena todas as perguntas no vetor 'header'. */
        protocol.pages.forEach((page) => {
            page.itemGroups.forEach((itemGroup) => {
                const items = itemGroup.items;
                switch (itemGroup.type) {
                    case 'ONE_DIMENSIONAL':
                        items.forEach((item) => {
                            header.push(String(item.text).replace(/\n/g, ' '));
                            totalItems++;
                        });
                        break;
                    case 'TEXTBOX_TABLE':
                    case 'RADIO_TABLE':
                    case 'CHECKBOX_TABLE':
                        const tableColumns = itemGroup.tableColumns;
                        items.forEach((item) => {
                            tableColumns.forEach((column) => {
                                let cleanText = `Table${countTables}_${String(item.text).replace(/\n/g, ' ').replace(/\s+/g, '')}_${String(
                                    column.text
                                )
                                    .replace(/\n/g, ' ')
                                    .replace(/\s+/g, '')}`;
                                header.push(cleanText);
                                totalItems++;
                            });
                        });
                        countTables++;
                        break;
                    default:
                        break;
                }
            });
        });

        /** Coloca tudo como uma string única dividindo as perguntas com o divisor de colunas do csv: ';' */
        const headerString = `"${header.join('";"')}"`;

        /** Armazena as respostas. */
        ansIds.forEach((ansId, index) => {
            let userAnswers = [];
            userAnswers[0] = `"${String(ans.answers[ansId].user.username)}`;
            const rawStatus = String(ans.answers[ansId].approvalStatus);
            userAnswers[1] = String(statusMapping[rawStatus] || '');
            totalItems = 2;
            countTables = 0;

            protocol.pages.forEach((page) => {
                page.itemGroups.forEach((itemGroup) => {
                    const items = itemGroup.items;
                    items.forEach((item) => {
                        let cleanText = ' ';
                        switch (itemGroup.type) {
                            case 'ONE_DIMENSIONAL':
                                switch (item.type) {
                                    case 'TIMEBOX':
                                    case 'DATEBOX':
                                    case 'LOCATIONBOX':
                                    case 'NUMBERBOX':
                                    case 'TEXTBOX':
                                    case 'RANGE':
                                        cleanText = ' ';
                                        Object.entries(item.itemAnswers || {}).forEach(([applicationAnswerId, answerGroup]) => {
                                            if (applicationAnswerId === ansId) {
                                                Object.entries(answerGroup || {}).forEach(([_, groupAnswers]) => {
                                                    groupAnswers.forEach((groupAnswer) => {
                                                        cleanText = String(groupAnswer.text || '').replace(/\n/g, ' ');
                                                    });
                                                });
                                            }
                                        });
                                        userAnswers[totalItems] = cleanText;
                                        break;
                                    case 'TEXT':
                                        userAnswers[totalItems] = 'Enunciado*';
                                        break;
                                    case 'CHECKBOX':
                                    case 'SELECT':
                                    case 'RADIO':
                                        const currentTotalItems = totalItems;
                                        userAnswers[currentTotalItems + 1] = '';
                                        item.itemOptions.forEach((option, optionIndex) => {
                                            Object.entries(option.optionAnswers || {}).forEach(([applicationAnswerId, answerGroup]) => {
                                                if (applicationAnswerId === ansId) {
                                                    Object.entries(answerGroup || {}).forEach(([_, groupAnswers]) => {
                                                        groupAnswers.forEach((groupAnswer) => {
                                                            if (groupAnswer.text !== undefined) {
                                                                userAnswers[currentTotalItems + 1] +=
                                                                    userAnswers[currentTotalItems + 1] !== ''
                                                                        ? ` | ${String(option.text).replace(/\n/g, ' ')}`
                                                                        : String(option.text).replace(/\n/g, ' ');
                                                            }
                                                        });
                                                    });
                                                }
                                            });
                                        });
                                        break;
                                    case 'UPLOAD':
                                        Object.entries(item.itemAnswers || {}).forEach(([applicationAnswerId, answerGroup]) => {
                                            if (applicationAnswerId === ansId) {
                                                Object.entries(answerGroup || {}).forEach(([_, groupAnswers]) => {
                                                    groupAnswers.forEach((groupAnswer) => {
                                                        const uploadFiles = groupAnswer.files || [];
                                                        const imageFiles = uploadFiles.filter((file) => {
                                                            const filePath = process.env.REACT_APP_API_URL + 'api/' + file?.path || '';
                                                            return (
                                                                filePath.includes('.png') ||
                                                                filePath.includes('.jpg') ||
                                                                filePath.includes('.jpeg')
                                                            );
                                                        });
                                                        userAnswers[totalItems] = imageFiles
                                                            .map((file) => process.env.REACT_APP_API_URL + 'api/' + file.path)
                                                            .join(' | ');
                                                    });
                                                });
                                            }
                                        });
                                        userAnswers[totalItems] = userAnswers[totalItems] || '';
                                        break;
                                    default:
                                        break;
                                }
                                totalItems++;
                                break;
                            case 'RADIO_TABLE':
                            case 'CHECKBOX_TABLE':
                            case 'TEXTBOX_TABLE':
                                const tableColumns = itemGroup.tableColumns;
                                if (item.tableAnswers[ansId]) {
                                    Object.values(item.tableAnswers[ansId] || {}).forEach((rowAnswers) => {
                                        tableColumns.forEach((column) => {
                                            // Verifica se a tabela é do tipo RADIO_TABLE ou CHECKBOX_TABLE
                                            if (itemGroup.type === 'RADIO_TABLE' || itemGroup.type === 'CHECKBOX_TABLE') {
                                                // Se a resposta for uma string vazia, marca como "Selected"
                                                cleanText = rowAnswers[column.id] === '' ? 'Selected' : '';
                                            } else {
                                                cleanText = rowAnswers[column.id] || '';
                                            }
                                            userAnswers[totalItems] = cleanText;
                                            totalItems++;
                                        });
                                    });
                                } else {
                                    tableColumns.forEach((column) => {
                                        userAnswers[totalItems] = '';
                                        totalItems++;
                                    });
                                }
                                countTables++;
                                break;
                            default:
                                break;
                        }
                    });
                });
            });

            userAnswers[totalItems - 1] = `${userAnswers[totalItems - 1] || ''}"`;
            answers[index] = userAnswers.join('";"');
        });

        const csv = [headerString, ...answers].join('\r\n');
        return csv;
    };

    /**
     * Função para alternar o status de aprovação da resposta
     * Corresponde ao controller toggleApplicationAnswerApproval (API)
     * @param {string} applicationAnswerId - ID da resposta da aplicação a ser alterada
     * @param {string} targetStatus - Status de aprovação desejado ('APPROVED' ou 'REJECTED')
     */
    const toggleApprovalStatus = (applicationAnswerId, targetStatus) => {
        axios
            .put(
                `${process.env.REACT_APP_API_URL}api/applicationAnswer/toggleApplicationAnswerApproval/${applicationAnswerId}`,
                { status: targetStatus },
                { headers: { Authorization: `Bearer ${user.token}` } }
            )
            .then((response) => {
                showAlert({
                    headerText: targetStatus === 'APPROVED' ? 'Resposta aprovada com sucesso' : 'Resposta desaprovada com sucesso',
                    message: response.data.message,
                });
                setAnswer((prev) => {
                    const newAnswer = { ...prev };
                    newAnswer.answers[applicationAnswerId].approvalStatus = targetStatus;
                    return newAnswer;
                });
            })
            .catch((error) =>
                showAlert({
                    headerText: 'Erro ao alterar status da resposta',
                    message: error.response?.data.message || '',
                })
            );
    };

    /**
     * Retorna as configurações de exibição (rótulo, cores e ícone) com base no status de aprovação da resposta.
     * @param {string} approvalStatus - Status de aprovação da resposta ('APPROVED', 'REJECTED' ou 'PENDING')
     * @returns {Object} Configurações de exibição para o status fornecido
     */
    const getStatusConfig = (approvalStatus) => {
        if (approvalStatus === 'APPROVED') {
            return {
                label: 'Aprovada',
                colorHsl: [97, 43, 70],
                textColor: '#4e7d44',
                bgColor: '#e2f3dd',
                icon: 'check_circle',
            };
        } else if (approvalStatus === 'REJECTED') {
            return {
                label: 'Reprovada',
                colorHsl: [6, 84, 75],
                textColor: '#d65a50',
                bgColor: '#fde8e8',
                icon: 'cancel',
            };
        } else {
            return {
                label: 'Pendente',
                colorHsl: [197, 43, 61],
                textColor: '#3d7a92',
                bgColor: '#e1f5fe',
                icon: 'pending',
            };
        }
    };

    /**
     * Função para alternar a habilitação (Ativa/Inativa)
     * Corresponde ao controller toggleApplicationAnswerEnablement (API)
     * @param {string} applicationAnswerId - ID da resposta da aplicação a ser alterada
     */
    const toggleEnablement = async (applicationAnswerId) => {
        axios
            .put(
                `${process.env.REACT_APP_API_URL}api/applicationAnswer/toggleApplicationAnswerEnablement/${applicationAnswerId}`,
                { enabled: false },
                { headers: { Authorization: `Bearer ${user.token}` } }
            )
            .then((response) => {
                showAlert({
                    headerText: 'Resposta desativada com sucesso',
                    message: response.data.message,
                });
                setAnswer((prev) => {
                    const newAnswer = { ...prev };
                    newAnswer.answers[applicationAnswerId].enabled = false;
                    return newAnswer;
                });
            })
            .catch((error) =>
                showAlert({
                    headerText: 'Erro ao alterar status da resposta',
                    message: error.response?.data.message || '',
                })
            );
    };

    /**
     * Filtra as respostas para exibir apenas as que estão habilitadas (enabled !== false).
     */
    const enabledEntries = Object.entries(answer.answers).filter(([key, value]) => value.enabled !== false);

    /**
     * Filtra as respostas com base no status considerando somente as que estão habilitadas.
     */
    const filteredEntries = Object.entries(answer.answers)
        .filter(([key, value]) => value.enabled !== false)
        .filter(([key, value]) => {
            if (filterStatus === 'ALL') return true;
            if (filterStatus === 'PENDING') return value.approvalStatus === 'PENDING' || !value.approvalStatus;
            return value.approvalStatus === filterStatus;
        });

    const filteredBySearch = filteredEntries.filter(([key, value]) => value.user.username.toLowerCase().includes(searchTerm.toLowerCase()));

    /**
     * Componente interno para os botões de filtro
     */
    const FilterPill = ({ label, active, onClick, color }) => (
        <button
            onClick={onClick}
            className="btn rounded-pill border-0 transition-all px-3 py-1"
            style={{
                fontSize: '0.75rem',
                fontWeight: '500',
                backgroundColor: active ? color : '#f0f0f0',
                color: active ? '#fff' : '#535353',
                boxShadow: active ? `0 4px 10px ${color}44` : 'none',
                opacity: active ? 1 : 0.7,
            }}
        >
            {label}
        </button>
    );

    return (
        <div className="container-fluid d-flex flex-column flex-grow-1 p-0 m-0">
            <div className="row flex-grow-1 m-0">
                <div className="col-auto bg-coral-red d-flex position-lg-sticky vh-100 top-0 p-0">
                    <div className="offcanvas-lg offcanvas-start bg-coral-red w-auto d-flex" tabIndex="-1" id="sidebar">
                        <Sidebar showExitButton={false} />
                    </div>
                </div>
                <div className="col d-flex flex-column flex-grow-1 bg-white p-0">
                    <NavBar showNavTogglerMobile={true} showNavTogglerDesktop={false} />
                    <div className="row d-flex align-items-center justify-content-center font-barlow h-100 p-0 m-0">
                        <div className="col col-md-10 d-flex flex-column h-100 p-4 px-lg-5 pb-lg-4">
                            <div className="row rounded-4 p-0 m-0 mb-3">
                                <div className="col col-12 col-md-9 order-1 d-flex flex-column align-items-center align-items-md-start mb-2 mb-md-0 p-0">
                                    <div className="d-flex">
                                        <span className="color-gray">{`Aplicações`}</span>
                                        <MaterialSymbol icon="chevron_right" size={24} fill={1} grade={200} weight={400} className="mx-2" />
                                        <Link
                                            className="color-dark-gray text-center text-md-start mb-2 mb-md-0"
                                            to={`/dash/applications/${applicationId}`}
                                        >
                                            {answer.protocol.title}
                                        </Link>
                                    </div>

                                    <h4 className="fw-bold color-dark-gray">Gestão de Respostas</h4>
                                </div>
                                <div className="col col-12 col-md-3 order-2 d-flex align-items-center p-0">
                                    <TextButton
                                        className="shadow-sm"
                                        text={'Exportar'}
                                        hsl={[197, 43, 61]}
                                        onClick={() => createFile(modalRef)}
                                    />
                                </div>
                            </div>

                            <div className="bg-light-gray rounded-4 mb-3 p-3">
                                <h2 className="color-dark-gray fw-medium fs-5 m-0">{Object.keys(enabledEntries).length + ' respostas '}</h2>
                            </div>
                            {enabledEntries.length > 0 && (
                                <div className="bg-light-gray rounded-4 mb-3 p-3 pb-1">
                                    <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 m-0 mb-3">
                                        <div className="d-flex align-items-center gap-2 m-0">
                                            <MaterialSymbol icon="list_alt" size={24} color="#535353" fill={0} />
                                            <h2 className="color-dark-gray fw-medium fs-5 m-0">Respostas</h2>
                                            {(selectedAnswer !== undefined || selectedAnswer !== undefined) && (
                                                <a
                                                    href="#answerTab"
                                                    onClick={() => setVisualization(undefined, undefined)}
                                                    className="color-dark-gray fw-semibold fs-6"
                                                >
                                                    {'(ver todas)'}
                                                </a>
                                            )}
                                        </div>
                                        {answer.actions.toApproveAnswers === true && (
                                            <div className="d-flex align-items-center gap-1 gap-md-2 flex-wrap">
                                                <FilterPill
                                                    label="Todas"
                                                    active={filterStatus === 'ALL'}
                                                    onClick={() => setFilterStatus('ALL')}
                                                    color="#787878"
                                                />
                                                <FilterPill
                                                    label="Pendentes"
                                                    active={filterStatus === 'PENDING'}
                                                    onClick={() => setFilterStatus('PENDING')}
                                                    color="#71aec6"
                                                />
                                                <FilterPill
                                                    label="Aprovados"
                                                    active={filterStatus === 'APPROVED'}
                                                    onClick={() => setFilterStatus('APPROVED')}
                                                    color="#abd392"
                                                />
                                                <FilterPill
                                                    label="Reprovados"
                                                    active={filterStatus === 'REJECTED'}
                                                    onClick={() => setFilterStatus('REJECTED')}
                                                    color="#f5948a"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* BARRA DE PESQUISA ADICIONADA */}
                                    <div className="mb-3">
                                        <div className="position-relative">
                                            <MaterialSymbol
                                                icon="search"
                                                size={20}
                                                color="#787878"
                                                className="position-absolute top-50 start-0 translate-middle-y ms-3"
                                                style={{ zIndex: 2 }}
                                            />
                                            <input
                                                type="text"
                                                placeholder="Buscar por usuário..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="form-control rounded-pill py-2"
                                                style={{
                                                    paddingLeft: '45px',
                                                    fontSize: '14px',
                                                    border: '1px solid #dee2e6',
                                                }}
                                            />
                                            {searchTerm && (
                                                <button
                                                    onClick={() => setSearchTerm('')}
                                                    className="position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent me-3"
                                                    style={{ zIndex: 2 }}
                                                >
                                                    <MaterialSymbol icon="close" size={18} color="#787878" />
                                                </button>
                                            )}
                                        </div>
                                        {/* Indicador de resultados da busca */}
                                        {searchTerm && (
                                            <div className="mt-2 text-muted small">
                                                Encontrado {filteredBySearch.length} resultado{filteredBySearch.length !== 1 ? 's' : ''}{' '}
                                                para "{searchTerm}"
                                            </div>
                                        )}
                                    </div>

                                    {/* LISTA COM SCROLL E RESPONSIVIDADE MELHORADA */}
                                    <div
                                        style={{
                                            maxHeight: '500px',
                                            overflowY: 'auto',
                                            overflowX: 'hidden',
                                            paddingRight: '5px', // Espaço para a barra de rolagem
                                        }}
                                    >
                                        {filteredBySearch.length > 0 ? (
                                            filteredBySearch.map(([key, value]) => {
                                                const status = getStatusConfig(value.approvalStatus);
                                                return (
                                                    <div
                                                        key={'answer-' + key}
                                                        className="bg-white rounded-4 mb-3 p-2 px-3"
                                                        style={{
                                                            borderLeft: `5px solid hsl(${status.colorHsl[0]}, ${status.colorHsl[1]}%, ${status.colorHsl[2]}%)`,
                                                        }}
                                                    >
                                                        {/* MELHORIA DE RESPONSIVIDADE */}
                                                        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                                                            <div className="flex-grow-1">
                                                                <div className="d-flex align-items-center gap-2 flex-wrap justify-content-start">
                                                                    <a
                                                                        className="color-dark-gray fw-bold fs-6 text-decoration-none hover-opacity"
                                                                        href="#answerTab"
                                                                        onClick={() => {
                                                                            setVisualization(key, undefined);
                                                                            if (value.coordinate) {
                                                                                mapRef.current.setView([
                                                                                    value.coordinate.latitude,
                                                                                    value.coordinate.longitude,
                                                                                ]);
                                                                            }
                                                                        }}
                                                                        style={{ cursor: 'pointer' }}
                                                                    >
                                                                        {value.user.username}
                                                                    </a>
                                                                    {answer.actions.toApproveAnswers === true && (
                                                                        <span
                                                                            className="d-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-bold"
                                                                            style={{
                                                                                backgroundColor: status.bgColor,
                                                                                color: status.textColor,
                                                                                fontSize: '0.65rem',
                                                                                textTransform: 'uppercase',
                                                                                letterSpacing: '0.5px',
                                                                            }}
                                                                        >
                                                                            <MaterialSymbol icon={status.icon} size={14} fill={1} />
                                                                            {status.label}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2 mt-1">
                                                                    <MaterialSymbol icon="history" size={22} color="#787878" />{' '}
                                                                    <span className="color-gray fw-medium fs-6">
                                                                        Enviado em {new Date(value.date).toLocaleDateString()}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {/* BOTÕES COM CORES ATUALIZADAS E RESPONSIVOS */}
                                                            {answer.actions.toApproveAnswers === true && (
                                                                <div className="d-flex align-items-center gap-2 justify-content-start justify-content-lg-end">
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <RoundedButton
                                                                            hsl={
                                                                                value.approvalStatus === 'APPROVED'
                                                                                    ? [97, 43, 70]
                                                                                    : [97, 43, 50]
                                                                            }
                                                                            className="text-white"
                                                                            size={32}
                                                                            onClick={() =>
                                                                                value.approvalStatus !== 'APPROVED' &&
                                                                                toggleApprovalStatus(key, 'APPROVED')
                                                                            }
                                                                            icon="check"
                                                                            title="Aprovar"
                                                                        />
                                                                        <RoundedButton
                                                                            hsl={
                                                                                value.approvalStatus === 'REJECTED'
                                                                                    ? [6, 84, 75]
                                                                                    : [6, 84, 65]
                                                                            }
                                                                            className="text-white"
                                                                            size={32}
                                                                            onClick={() =>
                                                                                value.approvalStatus !== 'REJECTED' &&
                                                                                toggleApprovalStatus(key, 'REJECTED')
                                                                            }
                                                                            icon="close"
                                                                            title="Reprovar"
                                                                        />
                                                                    </div>
                                                                    <div className="d-flex align-items-center">
                                                                        <RoundedButton
                                                                            hsl={[355, 78, 55]} // VERMELHO para remover
                                                                            className="text-white"
                                                                            size={32}
                                                                            onClick={() => toggleEnablement(key)}
                                                                            icon="delete"
                                                                            title="Remover"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="text-center py-5 text-muted">
                                                <MaterialSymbol icon="search_off" size={48} color="#787878" />
                                                <p className="mt-2 mb-0">Nenhuma resposta encontrada</p>
                                                {searchTerm && <small>Não encontramos resultados para "{searchTerm}"</small>}
                                            </div>
                                        )}
                                    </div>

                                    {/* Mostrar total de resultados */}
                                    {filteredBySearch.length > 0 && (
                                        <div className="mt-3 text-center text-muted small">
                                            Mostrando {filteredBySearch.length} de {filteredEntries.length} respostas
                                        </div>
                                    )}
                                </div>
                            )}
                            {answer.keepLocation && (
                                <div id="answerTab" className="bg-light-gray rounded-4 mb-3 p-3 pb-1">
                                    <h2 className="d-block color-dark-gray fw-medium fs-5 mb-3">Localizações</h2>
                                    <div className="rounded-4 overflow-hidden bg-white mb-3">
                                        <MapContainer center={[-14.235, -51.9253]} zoom={3} style={{ height: '400px' }} ref={mapRef}>
                                            <TileLayer
                                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            />
                                            {enabledEntries
                                                .filter(([_, value]) => value.coordinate)
                                                .map(([key, value]) => {
                                                    return selectedAnswer === undefined || selectedAnswer === key ? (
                                                        <Marker
                                                            position={[value.coordinate.latitude, value.coordinate.longitude]}
                                                            key={'marker-' + key}
                                                            eventHandlers={{
                                                                add: () =>
                                                                    mapRef.current.setView([
                                                                        value.coordinate.latitude,
                                                                        value.coordinate.longitude,
                                                                    ]),
                                                            }}
                                                        >
                                                            <Popup>
                                                                <a
                                                                    className="color-dark-gray fw-bold"
                                                                    href="#answerTab"
                                                                    onClick={() => setVisualization(key, undefined)}
                                                                >
                                                                    {value.user.username +
                                                                        ' - ' +
                                                                        new Date(value.date).toLocaleDateString()}
                                                                </a>
                                                            </Popup>
                                                        </Marker>
                                                    ) : null;
                                                })}
                                        </MapContainer>
                                    </div>
                                </div>
                            )}

                            <div className=" mb-lg-4">
                                {answer.protocol.pages.map((page, pageIndex) => {
                                    return page.itemGroups.map((itemGroup, itemGroupIndex) => {
                                        return (() => {
                                            switch (itemGroup.type) {
                                                case 'ONE_DIMENSIONAL':
                                                    return itemGroup.items.map((item, itemIndex) => {
                                                        const enabledItemAnswers = Object.entries(item.itemAnswers).filter(
                                                            ([applicationAnswerId]) =>
                                                                answer.answers[applicationAnswerId]?.enabled !== false
                                                        );

                                                        return (
                                                            (selectedItem === undefined || selectedItem === item.id) &&
                                                            item.type !== 'TEXT' && (
                                                                <div
                                                                    key={'input-' + item.id}
                                                                    className="bg-light-gray rounded-4 mb-3 p-3 pb-1"
                                                                >
                                                                    <a
                                                                        href="#answerTab"
                                                                        onClick={() => setVisualization(undefined, item.id)}
                                                                        className="d-block color-dark-gray fw-medium fs-5 mb-3"
                                                                    >
                                                                        {item.text}
                                                                    </a>
                                                                    {enabledItemAnswers.map(([applicationAnswerId, answerGroupId]) => {
                                                                        return Object.entries(answerGroupId).map(
                                                                            ([answerGroupId, groupAnswers]) => {
                                                                                return groupAnswers.map((groupAnswer) => {
                                                                                    return (
                                                                                        (selectedAnswer === undefined ||
                                                                                            selectedAnswer === applicationAnswerId) && (
                                                                                            <div
                                                                                                key={'answer-' + applicationAnswerId}
                                                                                                className="bg-white rounded-4 mb-3 p-2 px-3"
                                                                                            >
                                                                                                <p className="fw-medium fs-6 m-0 mb-1">
                                                                                                    {answer.answers[applicationAnswerId]
                                                                                                        .user.username +
                                                                                                        ' - ' +
                                                                                                        new Date(
                                                                                                            answer.answers[
                                                                                                                applicationAnswerId
                                                                                                            ].date
                                                                                                        ).toLocaleDateString()}
                                                                                                </p>
                                                                                                {item.type === 'UPLOAD' ? (
                                                                                                    <Gallery
                                                                                                        className="mb-3"
                                                                                                        item={{
                                                                                                            files: groupAnswer.files.map(
                                                                                                                (file) => ({
                                                                                                                    path: file.path,
                                                                                                                })
                                                                                                            ),
                                                                                                        }}
                                                                                                        galleryModalRef={galleryModalRef}
                                                                                                    />
                                                                                                ) : (
                                                                                                    <p className="fw-medium fs-6 color-dark-gray m-0">
                                                                                                        {groupAnswer.text}
                                                                                                    </p>
                                                                                                )}
                                                                                            </div>
                                                                                        )
                                                                                    );
                                                                                });
                                                                            }
                                                                        );
                                                                    })}
                                                                    {Object.entries(item.itemAnswers).length === 0 &&
                                                                        item.itemOptions.length === 0 && (
                                                                            <div className="bg-white rounded-4 mb-3 p-2 px-3">
                                                                                <p className="fw-medium fs-6 color-dark-gray m-0">
                                                                                    0 respostas
                                                                                </p>
                                                                            </div>
                                                                        )}
                                                                    {item.itemOptions.map((option, index) => {
                                                                        return (
                                                                            <div
                                                                                key={'answer-' + option.id}
                                                                                className="bg-white rounded-4 mb-3 p-2 px-3"
                                                                            >
                                                                                <p className="fw-medium fs-6 m-0 mb-1">
                                                                                    {option.text +
                                                                                        ' - ' +
                                                                                        (selectedAnswer === undefined
                                                                                            ? Object.keys(option.optionAnswers).length
                                                                                            : option.optionAnswers[selectedAnswer] ===
                                                                                              undefined
                                                                                            ? '0'
                                                                                            : '1') +
                                                                                        ' respostas'}
                                                                                </p>
                                                                                {Object.entries(option.optionAnswers).map(
                                                                                    ([applicationAnswerId, answerGroupId]) => {
                                                                                        return Object.entries(answerGroupId).map(
                                                                                            ([answerGroupId, groupAnswers]) => {
                                                                                                return (
                                                                                                    (selectedAnswer === undefined ||
                                                                                                        selectedAnswer ===
                                                                                                            applicationAnswerId) && (
                                                                                                        <p
                                                                                                            className="fw-medium fs-6 color-dark-gray m-0"
                                                                                                            key={
                                                                                                                'answer-' +
                                                                                                                applicationAnswerId +
                                                                                                                '-' +
                                                                                                                answerGroupId
                                                                                                            }
                                                                                                        >
                                                                                                            {answer.answers[
                                                                                                                applicationAnswerId
                                                                                                            ].user.username +
                                                                                                                ' (' +
                                                                                                                new Date(
                                                                                                                    answer.answers[
                                                                                                                        applicationAnswerId
                                                                                                                    ].date
                                                                                                                ).toLocaleDateString() +
                                                                                                                '); '}
                                                                                                        </p>
                                                                                                    )
                                                                                                );
                                                                                            }
                                                                                        );
                                                                                    }
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    {item.itemOptions.length > 0 && selectedAnswer === undefined && (
                                                                        <div className="rounded-4 overflow-hidden mb-3">
                                                                            <Chart
                                                                                chartType="PieChart"
                                                                                data={[['Resposta', 'Quantidade']].concat(
                                                                                    item.itemOptions.map((io) => {
                                                                                        return [
                                                                                            io.text,
                                                                                            Object.keys(io.optionAnswers).length,
                                                                                        ];
                                                                                    })
                                                                                )}
                                                                                options={{
                                                                                    chartArea: { top: 8, height: '90%', width: '90%' },
                                                                                    legend: { position: 'bottom' },
                                                                                    colors: [
                                                                                        '#F59489',
                                                                                        '#91CAD6',
                                                                                        '#FECF86',
                                                                                        '#4E9BB9',
                                                                                        '#EC6571',
                                                                                        '#AAD390',
                                                                                        '#8C6A80',
                                                                                        '#70A6A6',
                                                                                        '#FACD63',
                                                                                        '#578AA2',
                                                                                        '#E64E5E',
                                                                                        '#91BD7E',
                                                                                        '#F9A98F',
                                                                                        '#76C4D1',
                                                                                        '#FEDB8A',
                                                                                        '#5C97B2',
                                                                                        '#D85A6A',
                                                                                        '#89A86B',
                                                                                        '#F7BC92',
                                                                                        '#6FACB5',
                                                                                        '#E14953',
                                                                                        '#A1C588',
                                                                                        '#F5D39A',
                                                                                        '#568BA5',
                                                                                        '#C44D59',
                                                                                        '#7FA569',
                                                                                        '#E6A17C',
                                                                                        '#619BB0',
                                                                                        '#F39C9F',
                                                                                        '#ADC192',
                                                                                        '#FFD07D',
                                                                                        '#5499AE',
                                                                                        '#E6606F',
                                                                                        '#97BD83',
                                                                                        '#F6C2A3',
                                                                                        '#6BA3B8',
                                                                                        '#E15661',
                                                                                        '#90B67A',
                                                                                        '#F7AE9C',
                                                                                        '#75BFCB',
                                                                                        '#E6737D',
                                                                                        '#A2CA93',
                                                                                        '#FAD4AC',
                                                                                        '#639FB7',
                                                                                        '#D45B66',
                                                                                        '#83AB77',
                                                                                        '#F9B69B',
                                                                                        '#71B4C6',
                                                                                        '#E75762',
                                                                                        '#A8C599',
                                                                                    ],
                                                                                    is3D: true,
                                                                                }}
                                                                                height={'400px'}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )
                                                        );
                                                    });
                                                case 'TEXTBOX_TABLE':
                                                case 'RADIO_TABLE':
                                                case 'CHECKBOX_TABLE':
                                                    return enabledEntries.map(([key, value], index) => {
                                                        return (
                                                            (selectedAnswer === undefined || selectedAnswer === key) && (
                                                                <div key={`group-${itemGroupIndex}-${index}`} className="mb-3">
                                                                    <TableInput
                                                                        applicationAnswerId={key}
                                                                        group={itemGroup}
                                                                        answersPage={true}
                                                                        disabled={true}
                                                                        pageIndex={pageIndex + 1}
                                                                        groupIndex={itemGroupIndex + 1}
                                                                        answer={value}
                                                                    />
                                                                </div>
                                                            )
                                                        );
                                                    });
                                                default:
                                                    return null;
                                            }
                                        })();
                                    });
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <GalleryModal id="ProtocolPageGallery" ref={galleryModalRef} />
            <div className={`offcanvas offcanvas-start bg-coral-red w-auto d-flex`} tabIndex="-1" id="sidebar">
                <Sidebar />
            </div>
            <style>{styles}</style>
        </div>
    );
}

export default AnswerPage;
